/**
 * BRAVE Books — Product Upsell Card
 *
 * Adds the upsell product to cart by programmatically submitting a hidden
 * Dawn product-form. One Click Upsell listens for these submits and opens
 * its own cart drawer the same way it does on PDPs.
 *
 * Each card on the page is scoped to its own instance so multiple upsells
 * don't share state.
 */
(function () {
  class BraveUpsellCard {
    constructor(root) {
      this.root = root;
      this.handle = root.dataset.productHandle;
      this.button = root.querySelector('[data-upsell-atc]');
      this.qtySelect = root.querySelector('[data-upsell-qty]');
      this.variantSelects = Array.from(root.querySelectorAll('[data-upsell-option]'));
      this.priceEl = root.querySelector('[data-upsell-price]');
      this.compareEl = root.querySelector('[data-upsell-compare]');
      this.savingsEl = root.querySelector('[data-upsell-savings]');
      this.imageEl = root.querySelector('[data-upsell-image]');
      this.statusEl = root.querySelector('[data-upsell-status]');
      this.productData = null;
      this.originalButtonText = this.button ? this.button.textContent.trim() : 'Add to Cart';
      root._braveUpsellInstance = this;

      this.init();
    }

    init() {
      this.variantSelects.forEach((select) => {
        select.addEventListener('change', () => this.onVariantChange());
      });
      if (this.button) {
        this.button.addEventListener('click', (e) => this.onAdd(e));
      }
    }

    async loadProduct() {
      if (this.productData) return this.productData;
      const res = await fetch(`${window.Shopify.routes.root}products/${this.handle}.js`);
      if (!res.ok) throw new Error('Failed to load product');
      this.productData = await res.json();
      return this.productData;
    }

    async onVariantChange() {
      try {
        const data = await this.loadProduct();
        const selectedOptions = this.variantSelects.map((s) => s.value);

        // Match every option position, in order. Fixes the original bug where
        // `variant.options.indexOf(option) >= 0` could match the wrong variant
        // on multi-option products.
        const variant = data.variants.find((v) =>
          v.options.every((opt, i) => opt === selectedOptions[i])
        );
        if (!variant) return;

        this.button.dataset.variantId = variant.id;

        if (this.priceEl) this.priceEl.textContent = this.money(variant.price);

        if (this.compareEl) {
          const hasCompare = variant.compare_at_price && variant.compare_at_price > variant.price;
          this.compareEl.hidden = !hasCompare;
          if (hasCompare) {
            this.compareEl.textContent = this.money(variant.compare_at_price);
            if (this.savingsEl) {
              const pct = Math.round(
                ((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100
              );
              this.savingsEl.textContent = `Save ${pct}%`;
              this.savingsEl.hidden = false;
            }
          } else if (this.savingsEl) {
            this.savingsEl.hidden = true;
          }
        }

        if (this.imageEl && variant.featured_image && variant.featured_image.src) {
          this.imageEl.src = variant.featured_image.src;
          this.imageEl.removeAttribute('srcset');
        }

        if (variant.available) {
          this.button.removeAttribute('disabled');
          this.button.textContent = this.originalButtonText;
        } else {
          this.button.setAttribute('disabled', '');
          this.button.textContent = 'Sold Out';
        }
      } catch (err) {
        console.error('[BRAVE upsell] variant change failed', err);
      }
    }

    money(cents) {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        return window.Shopify.formatMoney(cents, window.Shopify.money_format || '${{amount}}');
      }
      return `$${(cents / 100).toFixed(2)}`;
    }

    onAdd(e) {
  e.preventDefault();
  const variantId = this.button.dataset.variantId;
  const quantity = parseInt(this.qtySelect ? this.qtySelect.value : '1', 10) || 1;
  this.addToCart({ variantId, quantity, triggerButton: this.button });
}

addToCart({ variantId, quantity = 1, triggerButton } = {}) {
  variantId = variantId || (this.button && this.button.dataset.variantId);
  triggerButton = triggerButton || this.button;
  if (!variantId || !triggerButton || triggerButton.hasAttribute('disabled')) return;

  const isInternal = triggerButton === this.button;
  const originalText = triggerButton.textContent.trim();

  triggerButton.textContent = 'Adding…';
  triggerButton.setAttribute('disabled', '');
  if (isInternal) {
    this.variantSelects.forEach((s) => s.setAttribute('disabled', ''));
  }
  this.setStatus('Adding to cart');

  const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');

  const config = fetchConfig('javascript');
  config.headers['X-Requested-With'] = 'XMLHttpRequest';
  delete config.headers['Content-Type'];

  const formData = new FormData();
  formData.append('id', variantId);
  formData.append('quantity', quantity);
  if (cart) {
    formData.append('sections', cart.getSectionsToRender().map((s) => s.id));
    formData.append('sections_url', window.location.pathname);
    cart.setActiveElement(triggerButton);
  }
  config.body = formData;

  fetch(`${routes.cart_add_url}`, config)
    .then((res) => res.json())
    .then((response) => {
      if (response.status) {
        console.error('[BRAVE upsell] add to cart error', response);
        this.setStatus('Error adding to cart');
        triggerButton.textContent = originalText;
        triggerButton.removeAttribute('disabled');
        if (isInternal) this.variantSelects.forEach((s) => s.removeAttribute('disabled'));
        return;
      }
      triggerButton.textContent = '✓ Added';
      if (isInternal) this.variantSelects.forEach((s) => s.removeAttribute('disabled'));
      this.setStatus('Added to cart');
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && response.sections) {
        cartDrawer.renderContents(response);
      }
      setTimeout(() => {
        triggerButton.textContent = originalText;
        triggerButton.removeAttribute('disabled');
        if (isInternal) this.setStatus('');
      }, 2500);
    })
    .catch((err) => {
      console.error('[BRAVE upsell] add to cart failed', err);
      triggerButton.textContent = originalText;
      triggerButton.removeAttribute('disabled');
      if (isInternal) {
        this.variantSelects.forEach((s) => s.removeAttribute('disabled'));
      }
      this.setStatus('');
    });
}

    setStatus(msg) {
      if (this.statusEl) this.statusEl.textContent = msg;
    }
  }

  function init() {
    document.querySelectorAll('[data-brave-upsell-card]').forEach((root) => {
      if (root.dataset.upsellInit === '1') return;
      root.dataset.upsellInit = '1';
      new BraveUpsellCard(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor support
  document.addEventListener('shopify:section:load', init);

// Standalone Quick ATC — works anywhere on the site, no upsell card required.
// Markup:
//   <button data-brave-quick-atc data-variant-id="40123456789">Buy</button>
// Optional attributes:
//   data-quantity="2"        (default 1)
//   data-added-text="Donated!"  (default "✓ Added")
document.addEventListener('click', (e) => {
  const trigger = e.target.closest('[data-brave-quick-atc]');
  if (!trigger || trigger.hasAttribute('disabled')) return;
  e.preventDefault();

  const variantId = trigger.dataset.variantId;
  const quantity = parseInt(trigger.dataset.quantity || '1', 10) || 1;
  if (!variantId) {
    console.warn('[BRAVE quick ATC] missing data-variant-id on', trigger);
    return;
  }

  const originalText = trigger.textContent.trim();
  const addedText = trigger.dataset.addedText || '✓ Added';

  trigger.textContent = 'Adding…';
  trigger.setAttribute('disabled', '');

  const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');

  const config = fetchConfig('javascript');
  config.headers['X-Requested-With'] = 'XMLHttpRequest';
  delete config.headers['Content-Type'];

  const formData = new FormData();
  formData.append('id', variantId);
  formData.append('quantity', quantity);
  if (cart) {
    formData.append('sections', cart.getSectionsToRender().map((s) => s.id));
    formData.append('sections_url', window.location.pathname);
    cart.setActiveElement(trigger);
  }
  config.body = formData;

  fetch(`${routes.cart_add_url}`, config)
    .then((res) => res.json())
    .then((response) => {
      if (response.status) {
        console.error('[BRAVE quick ATC] error', response);
        trigger.textContent = originalText;
        trigger.removeAttribute('disabled');
        return;
      }
      trigger.textContent = addedText;
      trigger.removeAttribute('disabled');
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && response.sections) {
        cartDrawer.renderContents(response);
      }
      setTimeout(() => { trigger.textContent = originalText; }, 2500);
    })
    .catch((err) => {
      console.error('[BRAVE quick ATC] failed', err);
      trigger.textContent = originalText;
      trigger.removeAttribute('disabled');
    });
});
})();